import { useEffect, useState } from 'react'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { IException } from '../../../../base/models'
import { IAppleDepEnvironment } from '../models/models'
import { GetAppleDepEnvironmentApi } from '../services/appleDepApi'
import CustomersTab from './CustomersTab'
import DevicesTab from './DevicesTab'
import TransactionsTab from './TransactionsTab'
import '../styles.scss'

type AppleDepTab = 'devices' | 'customers' | 'transactions'

/**
 * APPLE DEP — bayinin son kullanıcıya sattığı Apple cihazlarını Apple Device
 * Enrollment'a bildirdiği ekran.
 *
 * Apple'a gönderim otomatik değildir: bayi cihazı seçer, müşterisinin Apple
 * Business Manager Organization ID'sini atar ve "Apple'a Gönder" butonuna basar.
 * Sonuç asenkron gelir; statü saatlik job ile ve bu ekrandaki "Statü Sorgula"
 * butonuyla güncellenir.
 */
function AppleDep(props: BaseComponentWithContextProps) {
  const [activeTab, setActiveTab] = useState<AppleDepTab>('devices')
  const [environment, setEnvironment] = useState<IAppleDepEnvironment | undefined>(undefined)
  const [customerVersion, setCustomerVersion] = useState(0)

  const loadEnvironment = async () => {
    try {
      const api: GetAppleDepEnvironmentApi = new GetAppleDepEnvironmentApi({})
      const result = await api.getEnvironment()
      setEnvironment(result)
    } catch (err: any) {
      const exception = err as IException
      // Ortam bilgisi alınamazsa ekran çalışmaya devam etsin; uyarı bandı gösterilmez.
      console.warn('Apple DEP ortam bilgisi alınamadı:', exception?.description ?? err)
    }
  }

  useEffect(() => {
    loadEnvironment()
  }, [])

  const tabs: { key: AppleDepTab; label: string }[] = [
    { key: 'devices', label: 'Cihazlarım' },
    { key: 'customers', label: 'Müşterilerim' },
    { key: 'transactions', label: 'Gönderimlerim' },
  ]

  return (
    <div className='appleDepPage'>
      <div className='appleDepHeader'>
        <h4>APPLE DEP — Device Enrollment</h4>
        <p className='appleDepHeaderText'>
          Index'ten satın aldığınız Apple cihazlarını, son kullanıcınızın Apple Business Manager hesabına
          tanımlamak için buradan Apple'a bildirebilirsiniz. Gönderim otomatik yapılmaz; cihazları seçip
          müşteri atadıktan sonra <strong>Apple'a Gönder</strong> butonuna basmanız gerekir.
        </p>
      </div>

      {environment && (
        <div className={`appleDepEnvBanner ${environment.IsLive === 1 ? 'appleDepEnvLive' : 'appleDepEnvTest'}`}>
          <span className='appleDepEnvTag'>{environment.IsLive === 1 ? 'CANLI ORTAM' : 'TEST ORTAMI'}</span>
          {environment.UseFakeClient && (
            <span className='appleDepEnvNote'>
              Apple bağlantısı simülasyon modunda. Gönderdiğiniz kayıtlar Apple'a ulaşmaz, yalnızca akış test edilir.
            </span>
          )}
          {environment.DealerResellerId && (
            <span className='appleDepEnvNote' title="Son kullanıcınızın Apple Business Manager hesabında bu ID'yi yetkili tedarikçi olarak eklemesi gerekir.">
              Index Reseller ID: {environment.DealerResellerId}
            </span>
          )}
        </div>
      )}

      {environment && !environment.DealerCanSend && (
        <div className='appleDepWarning'>
          <strong>Gönderim yapılamıyor.</strong> {environment.DealerBlockReason}
        </div>
      )}

      <ul className='appleDepTabs'>
        {tabs.map(tab => (
          <li
            key={tab.key}
            className={activeTab === tab.key ? 'appleDepTabActive' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </li>
        ))}
      </ul>

      <div className='appleDepTabContent'>
        {activeTab === 'devices' && (
          <DevicesTab
            {...props}
            environment={environment}
            customerVersion={customerVersion}
            onGoToCustomers={() => setActiveTab('customers')}
            onSent={() => setActiveTab('transactions')}
          />
        )}
        {activeTab === 'customers' && (
          <CustomersTab
            {...props}
            environment={environment}
            onCustomersChanged={() => setCustomerVersion(v => v + 1)}
            onEnvironmentChanged={loadEnvironment}
          />
        )}
        {activeTab === 'transactions' && <TransactionsTab {...props} />}
      </div>
    </div>
  )
}

export default componentWithContext(AppleDep)
